import React from "react";
import { useTranslation } from "react-i18next";
import CustomUserChip from "./CustomUserChip";
import CustomTooltip from "./CustomTooltip";

/**
 * @fileoverview Reusable component for displaying a titled section with optional icons and user information.
 * @email azapata@ibl.mx
 * @author Ferro Zapata Arnulfo
 * @jobTitle Full Stack Developer
 */

/**
 * CustomGenericTittleIconDataChip Component
 *
 * Renders a title with optional icons and a user chip component.
 * Layout and styling can be dynamically changed using props.
 *
 * @param {React.ElementType} IconAvatar - React component to be used as the user's avatar icon.
 * @param {React.ElementType} Icon - React component to be used as an icon next to the title.
 * @param {string} title - Translation key for the title text.
 * @param {string} key - Unique key used for the CustomUserChip component.
 * @param {string} name - Name of the user to display inside the CustomUserChip.
 * @param {boolean} [border=true] - If true, applies a bottom border style.
 * @param {string} iconColor - Custom color for icon.
 * @param {string} titleClass - Custom class for title.
 * @param {string} titleBoxClass - Custom class for div title.
 * @param badge - Optional badge component to display next to the title (Optional)
 * @param showAvatar - Optional show icon on customUserChip
 * @param className - Optional classname to principal container
 * @param chipClasses -Optional chipClassName
 * @param emptyDefaultText - Default visual text placeholder when name is null , void , undefined
 * @returns {JSX.Element} Rendered CustomGenericTittleIconDataChip component.
 */

const CustomGenericTittleIconDataChip = ({ IconAvatar, Icon, title, key, name,
                                             border = true, badge, iconColor = '', showAvatar = true,
                                             className = '', chipClasses = "w-100", titleClass = "font-color-general-950",
                                             titleBoxClass='',emptyDefaultText}) => {
    const { t } = useTranslation();
    return (
        <>
            <div className={`d-flex flex-column ${className}`}>
                <div className={`${border ? "border-bottom-shades-300" : ""}`}>
                    <div className={`mb-1 d-flex justify-content-start align-items-center`}>
                        {Icon && <Icon className={`mr-1 btn-icon-svg-20px ${iconColor}` }/>}
                        <div className={`d-flex flex-grow-1 minw-0 lh-sm ${titleBoxClass}`}>
                            <CustomTooltip
                                text={typeof title === "string" ? t(title) : title}
                                classes={`ibl-h4 ${titleClass} ibl-font-w-600 mr-2 text-ellipsis-100`}
                                showIfTruncated={true}
                                placement={"top"}
                            />
                        </div>
                        {badge}
                    </div>
                </div>
                {name && (
                    <div>
                        <CustomUserChip
                            key={key}
                            name={name}
                            showAvatar={showAvatar}
                            Icon={IconAvatar}
                            className={chipClasses}
                        />
                    </div>
                )}
                {(emptyDefaultText && !name) &&
                <div className={`flex-grow-1 minw-0`}>
                    <CustomTooltip
                        text={typeof emptyDefaultText === "string" ? t(emptyDefaultText) : emptyDefaultText}
                        classes={`fs-14px ibl-font-w-400 mr-2 text-ellipsis-100`}
                        showIfTruncated={true}
                        placement={"top"}
                    />
                </div>
                }
            </div>
        </>
    );
}

export default CustomGenericTittleIconDataChip;
